import { FC } from 'react';
import { Box, MenuItem, Select, SelectChangeEvent, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Trans, useTrans } from '@libs/i18n';
import { Assessment } from '@libs/types';

interface CompareToDropdownProps {
  versions: Assessment[]; // all versions in family
  activeVersionId: string;
  compareToId: string; // '' = none
  onChange: (id: string) => void;
}

export const CompareToDropdown: FC<CompareToDropdownProps> = observer(({
  versions,
  activeVersionId,
  compareToId,
  onChange,
}) => {
  const theme = useTheme();
  const { t } = useTrans();

  // Options exclude the active version, sorted newest-first
  const options = [...versions]
    .filter((v) => v.id !== activeVersionId)
    .sort((a, b) => b.version - a.version);

  if (options.length === 0) return null;

  const selectedVersion = options.find((v) => v.id === compareToId);

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="textSM_regular" sx={{ color: 'text.tertiary', whiteSpace: 'nowrap' }}>
        <Trans i18nKey="assessments.overview.versioning.compareTo.label" />
      </Typography>
      <Select
        size="small"
        value={compareToId}
        displayEmpty
        onChange={(e: SelectChangeEvent) => onChange(e.target.value)}
        renderValue={(val) => {
          if (!val) {
            return (
              <Typography component="span" sx={{ color: 'text.tertiary', fontSize: 13 }}>
                {t('assessments.overview.versioning.compareTo.none')}
              </Typography>
            );
          }
          if (selectedVersion) {
            const score = selectedVersion.score != null ? Math.round(selectedVersion.score) : null;
            return (
              <Typography component="span" sx={{ fontSize: 13, fontWeight: 500, color: 'text.primary' }}>
                Version {selectedVersion.version}{score != null ? ` (${score}%)` : ''}
              </Typography>
            );
          }
          return val;
        }}
        sx={{
          minWidth: 160,
          height: 32,
          fontSize: 13,
          borderRadius: '8px',
          backgroundColor: '#fff',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[300] },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[400] },
        }}
      >
        <MenuItem value="">
          <Typography component="span" sx={{ color: 'text.tertiary', fontSize: 13 }}>
            {t('assessments.overview.versioning.compareTo.none')}
          </Typography>
        </MenuItem>
        {options.map((v) => {
          const score = v.score != null ? Math.round(v.score) : null;
          return (
            <MenuItem key={v.id} value={v.id} sx={{ fontSize: 13 }}>
              Version {v.version}{score != null ? ` (${score}%)` : ''}
              {v.isLatest ? ` · ${t('assessments.overview.versioning.versionSelector.latest')}` : ''}
            </MenuItem>
          );
        })}
      </Select>
    </Box>
  );
});
